import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useApiClient } from "../lib/api-context.js";
import { classificationLabel, type ExpenseClassification } from "../lib/overhead-review.js";
import { relockFinancialQueries } from "../lib/query-invalidation.js";

export interface ExpenseProposal {
  id: string; summary: string; question: string | null;
  suggestedClassification: ExpenseClassification | null; reason: string; evidenceIds: string[];
  scope: { accountName: string; periodStart: string; periodEnd: string; currency: string; amount: number };
}
const base = "/business-costs/overhead/review/evidence";

export function ExpenseTreatmentQuestion({ groupId, snapshotId }: { groupId: string; snapshotId: string }): React.ReactElement {
  const api = useApiClient();
  const queryClient = useQueryClient();
  const [proposal, setProposal] = useState<ExpenseProposal | null>(null);
  const [answer, setAnswer] = useState("");
  const [history, setHistory] = useState<Array<{ question: string; answer: string }>>([]);
  const [applied, setApplied] = useState(false);
  const [skipped, setSkipped] = useState(false);
  const refresh = (): void => {
    relockFinancialQueries(queryClient);
    for (const key of ["overhead-review", "overhead", "overhead-history", "data-trust-warnings"]) {
      void queryClient.invalidateQueries({ queryKey: [key] });
    }
  };
  const ask = useMutation({
    mutationFn: async () => {
      const next = proposal?.question && answer.trim()
        ? [...history, { question: proposal.question, answer: answer.trim() }].slice(-5) : history;
      const result = await api.post<ExpenseProposal>(`${base}/groups/${encodeURIComponent(groupId)}/propose`, { snapshotId, answerHistory: next });
      return { result, next };
    },
    onSuccess: ({ result, next }) => { setProposal(result); setHistory(next); setAnswer(""); setSkipped(false); setApplied(false); },
  });
  const save = useMutation({
    mutationFn: (undo: boolean) => api.post(`${base}/proposals/${proposal?.id}/${undo ? "undo" : "apply"}`, {}),
    onSuccess: (_result, undo) => { setApplied(!undo); refresh(); },
  });
  const pending = ask.isPending || save.isPending;
  const error = (ask.error ?? save.error) as { detail?: { error?: string } } | null;
  return <div style={styles.box}>
    {!proposal && <>
      <strong style={{ fontSize: 13 }}>How should this expense count?</strong>
      <p style={styles.copy}>We can use this month’s checked totals and your answers to suggest a treatment.</p>
      <p style={styles.small}>OpenAI receives the expense category, dates, amounts, entry types, and answers you provide. Leave names and bank details out of your answer.</p>
      <button type="button" disabled={pending} onClick={() => ask.mutate()} style={styles.button}>{ask.isPending ? "Checking…" : "Help me review this expense"}</button>
    </>}
    {proposal && <ExpenseProposalPreview proposal={proposal} applied={applied} />}
    {proposal?.question && !skipped && <>
      <label htmlFor={`expense-purpose-${groupId}`} style={styles.copy}>{proposal.question}</label>
      <textarea id={`expense-purpose-${groupId}`} value={answer} onChange={(event) => setAnswer(event.target.value)} maxLength={1000}
        rows={3} disabled={pending} placeholder="For example: this pays our office team and repeats each month." style={styles.input} />
      <div style={styles.actions}>
        <button type="button" disabled={pending || !answer.trim()} onClick={() => ask.mutate()} style={styles.button}>{ask.isPending ? "Checking your answer…" : "Check my answer"}</button>
        <button type="button" disabled={pending} onClick={() => setSkipped(true)} style={styles.secondary}>Not sure — leave open</button>
      </div>
    </>}
    {skipped && <p role="status" style={styles.copy}>Left open. This expense still needs a decision.</p>}
    {proposal?.suggestedClassification && !proposal.question && <div style={styles.actions}>
      <button type="button" disabled={pending} onClick={() => save.mutate(applied)} style={styles.button}>
        {save.isPending ? "Saving…" : applied ? "Undo this treatment" : "Apply to this month"}
      </button>
      {!applied && <button type="button" disabled={pending} onClick={() => { setProposal(null); setHistory([]); setAnswer(""); }} style={styles.secondary}>Start over</button>}
    </div>}
    {error && <p role="alert" style={{ ...styles.copy, color: "var(--bad)" }}>{error.detail?.error ?? "We couldn't finish that step. Refresh the expense details and try again."}</p>}
  </div>;
}

export function ExpenseProposalPreview({ proposal, applied }: { proposal: ExpenseProposal; applied: boolean }): React.ReactElement {
  const amount = new Intl.NumberFormat("en-US", { style: "currency", currency: proposal.scope.currency }).format(proposal.scope.amount);
  return <>
    <p style={styles.copy}>{proposal.summary}</p>
    <p style={styles.copy}>{proposal.reason}</p>
    {proposal.suggestedClassification && <>
      <strong style={{ fontSize: 13 }}>{classificationLabel(proposal.suggestedClassification)}</strong>
      <p style={styles.copy}>Applies to {amount} in {proposal.scope.accountName}, from {proposal.scope.periodStart} to {proposal.scope.periodEnd}.</p>
      <p style={styles.small}>Only this month’s treatment changes. Other months and your QuickBooks records stay as they are. Final profit approval is still separate.</p>
    </>}
    {!proposal.question && !proposal.suggestedClassification && <p style={styles.copy}>This expense remains open. You can review its month-specific split on this page.</p>}
    {applied && <p role="status" style={{ ...styles.copy, color: "var(--good)" }}>Treatment saved for this month.</p>}
  </>;
}
const styles: Record<string, React.CSSProperties> = {
  box: { borderTop: "1px solid var(--border)", marginTop: 14, paddingTop: 14 },
  copy: { display: "block", fontSize: 13, color: "var(--body)", lineHeight: 1.5, margin: "7px 0" },
  small: { fontSize: 12, color: "var(--faint)", lineHeight: 1.5, margin: "7px 0" },
  input: { boxSizing: "border-box", display: "block", width: "100%", maxWidth: 600, border: "1px solid var(--border)", borderRadius: 6, padding: 10, background: "var(--surface)", color: "var(--body)", font: "inherit", fontSize: 13 },
  actions: { display: "flex", flexWrap: "wrap", gap: 8, marginTop: 10 },
  button: { border: 0, borderRadius: 6, padding: "8px 12px", background: "var(--accent)", color: "var(--accent-contrast)", fontSize: 12, fontWeight: 650, cursor: "pointer" },
  secondary: { border: "1px solid var(--border)", borderRadius: 6, padding: "8px 12px", background: "var(--surface)", color: "var(--body)", fontSize: 12, cursor: "pointer" },
};
